import axios from "axios";
import { GET_ASSIGNED_QUESTIONS_URL, GET_CSRF_TOKEN } from "@/utils/constants/constants";

export default {
  computed: {
    filtered_questions() {
      if (!this.question_search.trim()) {
        return this.questions;
      }

      const search = this.question_search.toLowerCase();

      return this.questions.filter(q => {
        return (
          (q.question_text || "").toLowerCase().includes(search) ||
          (q.form_name || "").toLowerCase().includes(search) ||
          this.formatQuestionType(q.question_type).toLowerCase().includes(search)
        );
      });
    },

    hasSelectQuestions() {
      return this.questions.some(q => this.isSelectQuestion(q.question_type));
    },

    hasIncompleteSelectAssignments() {
      return this.pending_changes.some(change => {
        if (!change.should_assign) return false;

        const question = this.questions.find(q => q.id === change.question_id);
        if (!question || !this.isSelectQuestion(question.question_type)) return false;

        if (this.isSingleSelect(question.question_type)) {
          return !change.selected_option_id;
        }

        if (this.isMultiSelect(question.question_type)) {
          return !change.selected_option_ids || change.selected_option_ids.length === 0;
        }

        return false;
      });
    },

    hasPendingChanges() {
      return this.pending_changes.length > 0;
    },
  },

  watch: {
    is_visible(val) {
      if (val) {
        this.fetchCsrfToken();
        this.loadQuestions();

        this.$nextTick(() => {
          this.$refs.modalElement?.focus();
        });
      }
    },

    selected_condition: {
      handler() {
        if (this.is_visible) {
          this.loadQuestions();
        }
      },
      deep: true,
    },
  },

  methods: {
    attemptClose() {
      if (this.pending_changes.length > 0) {
        if (confirm("You have unsaved changes. Close without saving?")) {
          this.$emit("close");
        }
      } else {
        this.$emit("close");
      }
    },

    fetchCsrfToken() {
      axios.get(GET_CSRF_TOKEN, { withCredentials: true }).then(response => {
        this.csrfToken = response.data.csrfToken || response.data.token;
      });
    },

    loadQuestions() {
      if (!this.selected_rule) {
        this.questions = [];
        return;
      }

      this.is_loading = true;

      this.token = sessionStorage.getItem("token") || localStorage.getItem("token");

      axios
        .get(GET_ASSIGNED_QUESTIONS_URL, {
          headers: this.getHeaders(),
          params: { rule_id: this.selected_rule.id },
          withCredentials: true,
        })
        .then(response => {
          if (response.data.status !== "success") {
            this.questions = [];
            return;
          }

          this.questions = this.processQuestionsWithDependents(response.data.questions);

          this.updateQuestionAssignments();

          this.initializeSelectedOptions();

          this.pending_changes = [];
        })
        .catch(() => {
          this.questions = [];
        })
        .finally(() => {
          this.is_loading = false;
        });
    },

    updateQuestionAssignments() {
      this.questions.forEach(question => {
        const currentAssignment =
          question.field_assignments?.find(
            assignment => assignment.compliance_condition_id === this.selected_condition.id
          ) ||
          (question.condition && question.condition.id === this.selected_condition.id
            ? question.condition
            : null);

        const isCurrentCondition = !!currentAssignment;

        question.is_assigned = isCurrentCondition;
        question.assigned_to_condition = isCurrentCondition;

        if (question.dependent_questions) {
          question.dependent_questions.forEach(dep => {
            dep.assigned_to_condition = isCurrentCondition;
          });
        }

        if (isCurrentCondition && this.isSelectQuestion(question.question_type)) {
          this.setSelectedOptionsFromCondition(question, currentAssignment);
        }
      });
    },

    setSelectedOptionsFromCondition(question, assignmentSource) {
      const conditionData = assignmentSource.compliance_condition || assignmentSource;

      if (!conditionData || !conditionData.selected_options) return;

      const selectedOptions = conditionData.selected_options;

      if (this.isSingleSelect(question.question_type) && selectedOptions.length > 0) {
        question.selected_option_id = selectedOptions[0].id;
      }

      if (this.isMultiSelect(question.question_type)) {
        question.selected_option_ids = selectedOptions.map(option => option.id);
      }
    },

    getCurrentConditionSelectedOptions(question) {
      const hasCondition = question.condition !== null;
      const isCurrentCondition =
        hasCondition && this.selected_condition && question.condition.id === this.selected_condition.id;

      if (!isCurrentCondition || !question.condition || !question.condition.selected_options) {
        return null;
      }

      return question.condition.selected_options;
    },

    applySelectDefaults(question) {
      if (this.isSingleSelect(question.question_type)) {
        question.selected_option_id = question.selected_option_id || null;
      }

      if (this.isMultiSelect(question.question_type)) {
        question.selected_option_ids = Array.isArray(question.selected_option_ids)
          ? question.selected_option_ids
          : [];
      }
    },

    applyConditionSelectedOptions(question, selectedOptions) {
      if (this.isSingleSelect(question.question_type) && selectedOptions.length > 0) {
        question.selected_option_id = selectedOptions[0].id;
        return;
      }

      if (this.isMultiSelect(question.question_type)) {
        question.selected_option_ids = selectedOptions.map(option => option.id);
      }
    },

    initializeQuestionSelectedOptions(question) {
      if (!this.isSelectQuestion(question.question_type)) {
        return;
      }

      const selectedOptions = this.getCurrentConditionSelectedOptions(question);
      if (!selectedOptions) {
        this.applySelectDefaults(question);
        return;
      }

      this.applyConditionSelectedOptions(question, selectedOptions);
    },

    initializeSelectedOptions() {
      this.questions.forEach(question => {
        this.initializeQuestionSelectedOptions(question);
      });
    },

    isSelectQuestion(questionType) {
      if (!questionType) return false;
      const typeName = typeof questionType === "object" ? questionType.name : questionType;
      return ["single_select", "multi_select", "dropdown", "radio", "checkbox"].some(
        selectType => typeName.toLowerCase().includes(selectType)
      );
    },

    isSingleSelect(questionType) {
      if (!questionType) return false;
      const typeName = typeof questionType === "object" ? questionType.name : questionType;
      return ["single_select", "dropdown", "radio"].some(selectType =>
        typeName.toLowerCase().includes(selectType)
      );
    },

    isMultiSelect(questionType) {
      if (!questionType) return false;
      const typeName = typeof questionType === "object" ? questionType.name : questionType;
      return ["multi_select", "checkbox"].some(selectType =>
        typeName.toLowerCase().includes(selectType)
      );
    },

    isQuestionInPendingChanges(question) {
      return this.pending_changes.some(change => change.question_id === question.id);
    },

    processQuestionsWithDependents(questions) {
      const mainQuestions = [];
      const dependentMap = new Map();

      questions.forEach(q => {
        this._processQuestion(q, mainQuestions, dependentMap);
      });

      this._attachDependentQuestions(mainQuestions, dependentMap);
      return mainQuestions;
    },

    _processQuestion(question, mainQuestions, dependentMap) {
      const parentId = this._getParentId(question);
      const isAssigned = this._isQuestionAssigned(question);

      if (this._isDependentQuestion(parentId)) {
        this._addToDependentMap(dependentMap, parentId, question, isAssigned);
      } else {
        this._addToMainQuestions(mainQuestions, question, isAssigned);
      }
    },

    _getParentId(question) {
      return (
        question.depends_on_question ??
        question.depends_on_question_id ??
        question.parent_question_id
      );
    },

    _isQuestionAssigned(question) {
      const hasCondition = question.condition !== null;
      const isCurrentCondition =
        hasCondition && this.selected_condition && question.condition.id === this.selected_condition.id;

      return (
        isCurrentCondition ||
        question.assigned_to_condition === true ||
        question.is_assigned === true
      );
    },

    _isDependentQuestion(parentId) {
      return Number.isInteger(parentId) && parentId > 0;
    },

    _addToDependentMap(dependentMap, parentId, question, isAssigned) {
      if (!dependentMap.has(parentId)) {
        dependentMap.set(parentId, []);
      }

      dependentMap.get(parentId).push({
        id: question.id,
        compliance_field_id: question.compliance_field_id,
        question_text: question.question_text,
        question_type: question.question_type,
        question_number: question.question_number,
        assigned_to_condition: isAssigned,
      });
    },

    _addToMainQuestions(mainQuestions, question, isAssigned) {
      const questionData = {
        id: question.id,
        compliance_field_id: question.compliance_field_id,
        question_text: question.question_text,
        question_type: question.question_type,
        question_number: question.question_number,
        form_name: question.form_name,
        assigned_to_condition: isAssigned,
        is_assigned: isAssigned,
        condition: question.condition,
        options: question.options || [],
        selected_option_id: question.selected_option_id || null,
        selected_option_ids: Array.isArray(question.selected_option_ids)
          ? question.selected_option_ids
          : [],
        dependent_questions: [],
      };

      mainQuestions.push(questionData);
    },

    _attachDependentQuestions(mainQuestions, dependentMap) {
      mainQuestions.forEach(q => {
        if (dependentMap.has(q.id)) {
          q.dependent_questions = dependentMap.get(q.id);
        }
      });
    },

    formatQuestionType(type) {
      if (!type) return "Unknown";
      if (typeof type === "object") return type.name || "Unknown";
      return type;
    },

    getHeaders() {
      const headers = { "Content-Type": "application/json" };
      if (this.token) headers.Authorization = `Token ${this.token}`;
      if (this.csrfToken) headers["X-CSRFToken"] = this.csrfToken;
      return headers;
    },
  },
};
